// Магазин и заказ звёздами — tech/03 §12, tech/05. Прототип: вкладка «Талоны», покупка «Пачки» (E12-T02, E12-T10, E12-T12).
import { z } from 'zod';
import { Grant, IsoTime, ItemDefId, ProductId, Slot, Uuid7, strict } from '../common.js';
import { endpoint } from '../endpoint.js';
import { Item, ItemStats, Wallet } from './models.js';

export const ShopProduct = strict({
  productId: ProductId,
  priceXtr: z.int().positive(),
  grant: Grant,
  badge: z.string().max(32).nullable(),
  limit: strict({ left: z.int().nonnegative(), total: z.int().positive(), perPlayer: z.int().positive() }).optional(),
  endsAt: IsoTime.optional(),
  blocked: z.enum(['spend_limit', 'payments_off']).optional(),
});

/** Личные предложения (03 §12, `MOD-OFFER`): форма элемента в спецификации не задана (Р-02). */
export const PersonalOffer = strict({
  offerId: Uuid7,
  productId: ProductId,
  priceXtr: z.int().positive(),
  grant: Grant,
  expiresAt: IsoTime.nullable(),
});

/** Товар за талоны в витрине (economy/06 §4; заявка фронтенда-2, Р-86). У термоса `priceTalons` — цена следующего. */
export const TalonGoodView = strict({
  productId: z.string().regex(/^[A-Z0-9][A-Z0-9-]{1,63}$/),
  section: z.string().max(32),
  priceTalons: z.int().nonnegative(),
  limit: strict({ perDay: z.int().positive().optional(), perWeek: z.int().positive().optional(), lifetime: z.int().positive().optional() }).nullable(),
  used: z.int().nonnegative(),
  status: z.enum(['available', 'blocked']),
  reason: z.enum(['not_yet', 'paid_boxes_off', 'debt', 'limit']).optional(),
  /** «Ящик мастера»: открытий до гаранта эпической+ и легендарной (economy/04 §9). */
  pityLeft: strict({ epic: z.int().nonnegative(), legendary: z.int().nonnegative() }).optional(),
});

export const ShopResponse = strict({
  sections: z.array(strict({ id: z.string().min(1).max(32), products: z.array(ShopProduct) })),
  personalOffers: z.array(PersonalOffer),
  spendLimit: strict({ leftXtr: z.int().nonnegative(), resetsAt: IsoTime }).optional(),
  paymentsOff: z.boolean().optional(),
  // Заявка фронтенда-2 (Р-86): необязательны в схеме ради моков клиента; сервер отдаёт всегда.
  talonGoods: z.array(TalonGoodView).optional(),
  thermos: strict({ nextPriceTalons: z.int().positive().nullable(), leftToday: z.int().nonnegative(), perDay: z.int().positive() }).optional(),
  /** «Лавка Семёныча» недели: предмет на ступень (good, rare, epic, S8). */
  lavka: strict({
    items: z.array(strict({
      tier: z.enum(['good', 'rare', 'epic', 'S8']), productId: z.string(), defId: ItemDefId, priceTalons: z.int().positive(), purchased: z.boolean(),
      /** Характеристики, которые получит покупатель (+0, ролл 100%), — до покупки (E12-T21, Р-88). */
      stats: ItemStats.optional(),
    })),
    refreshAt: IsoTime,
  }).optional(),
  /** Ориентир цены в рублях за ★ (economy/04 §12 п. 4, `economy.rub_per_star_retail`). */
  rubPerStar: z.number().positive().optional(),
  /** Флаг `shop.paid_boxes` (ВР-10). */
  paidBoxes: z.boolean().optional(),
  /** Долг по талонам после возврата: траты талонов закрыты (tech/05 §8.4). */
  talonsDebt: z.int().nonnegative().optional(),
});

export const getShop = endpoint({
  id: 'shop.catalog', method: 'GET', path: '/shop', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: ShopResponse, status: 200, errors: [], spec: 'tech/03 §12; tech/05 §1.1', task: 'E12-T10',
});

export const createOrder = endpoint({
  id: 'shop.order.create', method: 'POST', path: '/shop/orders', auth: true, signed: true, idempotency: 'key-required', rate: 'shop',
  body: strict({ productId: ProductId, consentImmediateDelivery: z.boolean().optional() }),
  response: strict({ orderId: Uuid7, invoiceLink: z.url(), expiresAt: IsoTime }), status: 201,
  errors: ['PURCHASE_BLOCKED', 'TG_LINK_REQUIRED', 'SPEND_LIMIT_REACHED', 'FEATURE_DISABLED', 'ALREADY_PURCHASED',
    'SOLD_OUT', 'GONE', 'INVOICE_UNAVAILABLE'],
  spec: 'tech/03 §12; tech/05 §3, §3.1', task: 'E12-T02',
});

/** Статусы заказа (tech/02 §10 orders.status, tech/05 §2). */
export const OrderStatus = z.enum(['created', 'invoice_issued', 'pre_checkout_ok', 'paid', 'credited', 'cancelled', 'expired', 'refunded', 'failed']);

export const getOrder = endpoint({
  id: 'shop.order.get', method: 'GET', path: '/shop/orders/{orderId}', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: strict({ orderId: Uuid7 }),
  response: strict({ status: OrderStatus, credited: strict({ wallet: Wallet, grants: Grant }).optional() }), status: 200,
  errors: ['NOT_FOUND'], spec: 'tech/03 §12; tech/05 §7', task: 'E12-T02',
});

/** История покупок звёздами (`SCR-PURCHASES`; заявка фронтенда-2 «GET /shop/orders» — путь по tech/03 §12, Р-86). */
export const getPurchases = endpoint({
  id: 'shop.purchases', method: 'GET', path: '/shop/purchases', auth: true, signed: false, idempotency: 'read', rate: 'read',
  query: strict({ cursor: z.string().max(256).optional(), limit: z.coerce.number().int().min(1).max(50).optional() }),
  response: strict({
    items: z.array(strict({ orderId: Uuid7, productId: ProductId, priceXtr: z.int().positive(), status: OrderStatus, createdAt: IsoTime, creditedAt: IsoTime.nullable(), tgChargeId: z.string().nullable() })),
    nextCursor: z.string().max(256).nullable(),
  }), status: 200, errors: [], spec: 'tech/03 §12', task: 'E12-T02',
});

export const cancelOrder = endpoint({
  id: 'shop.order.cancel', method: 'POST', path: '/shop/orders/{orderId}/cancel', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: strict({ orderId: Uuid7 }), body: strict({}),
  response: strict({ status: z.literal('cancelled') }), status: 200,
  errors: ['NOT_FOUND', 'ALREADY_PAID'], spec: 'tech/03 §12; tech/05 §2', task: 'E12-T02',
});

/** Товары за талоны (economy/06 §4): ящики, ускорения, «Ведро гаек», «Лавка Семёныча», косметика, пропуск. */
export const buyForTalons = endpoint({
  id: 'shop.buy.talons', method: 'POST', path: '/shop/buy-for-talons', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({ productId: z.string().regex(/^[A-Z0-9][A-Z0-9-]{1,63}$/), qty: z.int().min(1).max(10) }),
  response: strict({ grants: Grant, wallet: Wallet }), status: 200,
  errors: ['TALONS_DEBT', 'GONE', 'NOT_ENOUGH_TALONS', 'DAILY_LIMIT', 'WEEKLY_LIMIT', 'NOT_FOUND'], spec: 'tech/03 §12; economy/06 §4', task: 'E12-T06',
});

/** «Витрина председателя» (X-17, economy/04 §9): своя у игрока на неделю, без случайности при покупке. */
export const getShowcase = endpoint({
  id: 'shop.showcase', method: 'GET', path: '/shop/showcase', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: strict({
    weekKey: z.string(),
    items: z.array(strict({
      pos: z.int().min(1).max(3), defId: ItemDefId, slot: Slot, itemLevel: z.int().positive(), priceTalons: z.int().positive(), purchased: z.boolean(),
      /** Характеристики выдаваемого предмета (+0, ролл 100%) — до покупки (E12-T21, Р-88). */
      stats: ItemStats.optional(),
    })),
    weekPurchases: z.int().nonnegative(), weeklyLimit: z.int().positive(), refreshAt: IsoTime,
  }), status: 200, errors: [], spec: 'tech/03 §12; economy/04 §9', task: 'E12-T20',
});

export const buyShowcase = endpoint({
  id: 'shop.showcase.buy', method: 'POST', path: '/shop/showcase/{pos}/buy', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: strict({ pos: z.coerce.number().int().min(1).max(3) }), body: strict({ weekKey: z.string() }),
  response: strict({ item: Item, wallet: Wallet }), status: 200,
  errors: ['TALONS_DEBT', 'ALREADY_PURCHASED', 'SHOWCASE_EXPIRED', 'NOT_ENOUGH_TALONS', 'WEEKLY_LIMIT'], spec: 'tech/03 §12; X-17', task: 'E12-T20',
});

/** Уведомление о возврате звёзд (tech/05 §8; заявка фронтенда-2, Р-86): показано и подтверждено игроком. */
export const RefundNotice = strict({ orderId: Uuid7, xtr: z.int().positive(), debitedTalons: z.int().nonnegative(), talonsDebt: z.int().nonnegative() });

export const ackRefundNotice = endpoint({
  id: 'shop.refund_notice.ack', method: 'POST', path: '/me/refund-notices/{orderId}/ack', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: strict({ orderId: Uuid7 }), body: strict({}), response: strict({ ok: z.literal(true) }), status: 200, errors: ['NOT_FOUND'], spec: 'tech/05 §8', task: 'E12-T03',
});
